import { defaultTheme } from 'react-admin';
import { createTheme, Theme } from '@mui/material/styles';
import { tokens } from './designTokens';
import { ADMINPANEL_PRIMARY, ADMINPANEL_PRIMARY_DARK } from './components/AdminPanelBrand';

/**
 * Theme-Aufbau aus der einzigen Token-Quelle (designTokens.ts) plus der
 * admin:panel-Markenfarbe.
 *
 * Seit 2026-09 ist der Admin produktuebergreifend (online:academy + azubi:web)
 * und traegt eine eigene Zwischenmarke der generation:l-Familie: admin:panel
 * (Kunden-Wunsch; vorher kurz generation:l-gebrandet). KEIN Mandanten-Branding
 * mehr — das lebt in der App. Primaerfarbe ist das admin:panel-Gruen (= generation:l-Gruen).
 */
const buildTheme = (primaryColor: string, secondaryColor: string): Theme => {
    const t = tokens;
    return createTheme({
        ...defaultTheme,
        palette: {
            primary: {
                main: primaryColor,
                dark: ADMINPANEL_PRIMARY_DARK,
                contrastText: '#ffffff',
            },
            secondary: {
                main: secondaryColor,
                dark: t.color.neutral900,
                contrastText: '#ffffff',
            },
            error: { main: t.color.error },
            warning: { main: t.warn.main, dark: t.warn.dark, contrastText: t.warn.contrastText },
            info: { main: t.color.infoDark },
            success: { main: '#1cab7e' },
            text: {
                primary: t.color.text.primary,
                secondary: t.color.text.secondary,
            },
            background: {
                default: t.color.background.default,
                paper: t.color.background.paper,
            },
            divider: t.color.divider,
        },
        shape: { borderRadius: t.shape.radiusSm },
        typography: {
            fontFamily: t.typography.fontFamily,
            h1: { ...t.typography.h1 },
            h2: { ...t.typography.h2 },
            h3: { ...t.typography.h3 },
            h4: { ...t.typography.h4 },
            h5: { ...t.typography.h5 },
            h6: { ...t.typography.h6 },
            body1: { ...t.typography.body1 },
            button: { fontWeight: t.typography.button.fontWeight, textTransform: t.typography.button.textTransform },
        },
        components: {
            MuiAppBar: {
                styleOverrides: {
                    root: {
                        background: t.color.background.default,
                        color: t.color.text.primary,
                        boxShadow: t.elevation.appBar,
                    },
                },
            },
            MuiButton: {
                styleOverrides: {
                    root: { borderRadius: t.shape.radiusSm, padding: '8px 16px', boxShadow: 'none' },
                },
                variants: [
                    { props: { variant: 'contained', color: 'warning' }, style: { backgroundColor: t.warn.dark, '&:hover': { backgroundColor: t.warn.darkHover } } },
                    { props: { variant: 'text', color: 'warning' }, style: { color: t.warn.dark } },
                    { props: { variant: 'outlined', color: 'warning' }, style: { color: t.warn.dark, borderColor: t.warn.dark } },
                ],
            },
            MuiDrawer: {
                styleOverrides: {
                    paper: { padding: 0 },
                },
            },
            // Admin-spezifisch: weiße Eingabefelder (deckungsgleich mit dem
            // !important-Override in azubi-web-styles.css).
            MuiInputBase: {
                styleOverrides: {
                    root: { backgroundColor: '#ffffff' },
                },
            },
            MuiChip: {
                variants: [
                    { props: { variant: 'filled', color: 'warning' }, style: { backgroundColor: t.warn.solidBg, color: t.warn.solidText } },
                    { props: { variant: 'outlined', color: 'warning' }, style: { backgroundColor: t.warn.surfaceBg, color: t.warn.surfaceText, borderColor: t.warn.surfaceBorder } },
                ],
            },
            MuiAlert: {
                styleOverrides: {
                    standardWarning: {
                        backgroundColor: t.warn.surfaceBg,
                        color: t.warn.surfaceText,
                        '& .MuiAlert-icon': { color: t.warn.main },
                    },
                },
            },
        },
    });
};

// Statisches admin:panel-Theme — Sekundaerfarbe ist das dunkle Slate der
// Site-Palette (Buttons/Chips mit neutraler Gewichtung).
export const theme = buildTheme(ADMINPANEL_PRIMARY, '#3a4c5a');
